import { useEffect } from 'react';
import { useSidebar } from '../contexts/SidebarContext';
import { useTopicList } from '../contexts/TopicListContext';
import { useViewport } from '../contexts/ViewportContext';
import { logger } from '../utils/logger';
import { useNavigate } from 'react-router-dom';

/**
 * Hook for handling keyboard shortcuts for sidebars and navigation
 * Sets up:
 * - 'p' or 'a' key shortcut to toggle both the project sidebar and topic list
 * - 'd' key shortcut to navigate to dashboard
 */
export function useSidebarToggleShortcut() {
  const { toggleCollapse: toggleProjectSidebar } = useSidebar();
  const { toggleCollapse: toggleTopicList } = useTopicList();
  const { isMobile } = useViewport();
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if input element is focused
      const activeElement = document.activeElement;
      const isInput =
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        activeElement?.isContentEditable;

      if (isInput) return;
      if (isMobile) return;

      const key = event.key ? event.key.toLowerCase() : 'unknown';

      // Handle 'p' or 'a' key for sidebar toggle
      if (key === 'p' || key === 'a') {
        // Log the keyboard shortcut
        logger.component(`Keyboard shortcut: ${key} - toggle sidebars`);

        // Toggle both sidebars
        toggleProjectSidebar();
        toggleTopicList();

        // Prevent default behavior
        event.preventDefault();
      }
      // Handle 'd' key for dashboard navigation
      else if (key === 'd') {
        // Log the keyboard shortcut
        logger.component('Keyboard shortcut: d - navigate to dashboard');

        // Navigate to dashboard
        navigate('/dashboard');

        // Prevent default behavior
        event.preventDefault();
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Clean up
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleProjectSidebar, toggleTopicList, isMobile, navigate]);
}
